import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Container } from '@pixi/react';
import * as PIXI from 'pixi.js';

import { isMobile } from '../../../../../core/helpers/isMobile';
import { isoStandard } from '../../../../../core/helpers/isoStandard';
import { setTranslationValues } from '../../../../../core/helpers/setTranslationValues';
import {
  GameRulesKeys,
  JackPotRulesKeys,
  licenseRulesKeys,
} from '../../../../../core/constants/gameRules';

import { jackPotStore } from '../../../../../store/jackPotStore';
import { userInfoStore } from '../../../../../store/userInfoStore';
import { applicationStore } from '../../../../../store/applicationStore';

import ScrollBox from '../../../../web/scrollBox';

import gameRulesCircle from '../../../../../assets/1x/web/gameRulesCircle.png';
import { getGameImages } from '../../../../../core/helpers/getGameImages';
import { gameStore } from '../../../../../store/gameStore';
import { gambleStore } from '../../../../../store/gambleStore';

const GameRulesContent = observer(({ x, y }) => {
  const { hasJackPot } = jackPotStore;
  const { hasGamble } = gambleStore;
  const { hasAutoBet } = gameStore;
  const { limits, currencySymbol, currencyIcon } = userInfoStore;
  const { canvasTranslationKeys, isShowLicenseMode, isDemo } = applicationStore;
  const handleGameRulesScrollBoxContent = useCallback(() => {
    let start = 0;
    return [...GameRulesKeys, ...(hasJackPot ? JackPotRulesKeys : []), ...licenseRulesKeys]
      .filter(
        (el) =>
          el.hasLicenseCheck !== isShowLicenseMode &&
          el.hasGambleCheck !== hasGamble &&
          el.hasAutoCheck !== hasAutoBet &&
          el.isDemoCheck !== isDemo
      )
      .map(
        ({
          speedText,
          soundsText,
          key,
          scale,
          type,
          valueKey,
          isCircle,
          isCurrency,
          isHide,
          isLicense,
          isSprite,
          textureName,
          licenseRules,
          isSmallSprite,
        }) => {
          const currency =
            isLicense === isShowLicenseMode && !isDemo
              ? `${currencyIcon}${isoStandard(limits[valueKey])}`
              : `${isoStandard(limits[valueKey])}${isCurrency ? currencySymbol : ''}`;
          const textValue = key
            ? setTranslationValues(`${canvasTranslationKeys[key]}`, {
                VALUE: currency,
              })
            : '';
          const textElementContainer = new PIXI.Container('TextElementContainer');
          const textElement = new PIXI.Text(textValue, {
            fontSize: type === 'title' ? 28 : 18,
            wordWrap: true,
            fontFamily: 'poppinsMedium',
            wordWrapWidth: isMobile() ? (licenseRules ? 400 : 500) : licenseRules ? 800 : 1062,
            fontWeight: type === 'title' ? 500 : 400,
            fill: type === 'title' ? '#FFD880' : '#FFFFFF',
          });

          textElement.x = licenseRules ? 90 : isCircle ? 11 : 0;
          textElement.y = Math.ceil(start);
          textElementContainer.addChild(textElement);

          if (isCircle) {
            const circle = PIXI.Sprite.from(gameRulesCircle);
            circle.x = 0;
            circle.y = Math.ceil(start + 10);
            circle.anchor.set(0);
            textElementContainer.addChild(circle);
          }
          if (isSprite) {
            textureName.forEach((el, index) => {
              if (getGameImages()[el]) {
                const sprite = PIXI.Sprite.from(getGameImages()[el]);
                sprite.scale.set(scale);
                sprite.x = index * 60;
                sprite.y = Math.ceil(start + (isSmallSprite ? 40 : 35));
                sprite.anchor.set(0);
                textElementContainer.addChild(sprite);
              }
            });
          }
          if (speedText && !isMobile()) {
            textElement.x = 200;
          } else if (speedText && isMobile()) {
            textElement.y = start + 70;
          }
          if (soundsText && !isMobile()) {
            textElement.x = 130;
          } else if (soundsText && isMobile()) {
            textElement.y = start + 50;
          }
          start = isMobile()
            ? textElement.y + textElement.height + (licenseRules ? 5 : 10)
            : textElement.y + textElement.height + (licenseRules ? 0 : 16);

          if (isHide) {
            textElementContainer.alpha = 0;
          }

          return textElementContainer;
        }
      );
  }, [
    isDemo,
    limits,
    hasGamble,
    hasAutoBet,
    hasJackPot,
    currencyIcon,
    currencySymbol,
    isShowLicenseMode,
    canvasTranslationKeys,
  ]);

  return (
    <Container x={x} y={y} name='GameRuleContainer'>
      <ScrollBox
        boxWidth={isMobile() ? 540 : 1440}
        boxHeight={isMobile() ? 740 : 600}
        scrollHeight={isMobile() ? 740 : 600}
        content={handleGameRulesScrollBoxContent()}
        scrollBarX={isMobile() ? 510 : 1100}
        scrollBarY={isMobile() ? 10 : 10}
        maxScrollBarY={isMobile() ? 50 : 10}
      />
    </Container>
  );
});

export default GameRulesContent;
